import {
	ExperiencePerformanceTypes,
	ExperienceTypes,
} from '@atlassian/ufo-experimental/experience-types';
import { getGlobalEventStream } from '@atlassian/ufo-experimental/global-stream-buffer/get-global-event-stream';
import { setGlobalEventStream } from '@atlassian/ufo-experimental/global-stream-buffer/set-global-event-stream';
import { ufolog } from '@atlassian/ufo-experimental/logger/ufolog';
import { ufowarn } from '@atlassian/ufo-experimental/logger/ufowarn';
import {
	type CustomData,
	type ExperienceData,
	type PageLoadExperienceData,
	PageVisibleState,
	type ExperiencePerformanceConfig as PerformanceConfig,
	UFOGlobalEventStreamEventType,
} from '@atlassian/ufo-experimental/types';
import { visibilityChangeObserver } from '@atlassian/ufo-experimental/visibility-change-observer';

import { UFO_BUILD_VERSION } from '../../buildVersion';
import {
	type AppConfig,
	type BundleEvalTimingsConfig,
	type ResourceTimingsConfig,
	TenantType,
} from '../../types';
import { UFOGlobalEventStream } from '../event-stream/event-stream';
import { type FeatureFlagClient, type SSRConfig } from '../types';
import { getPerformancePayload } from './performance/getPerformancePayload';
import { pageVisibleState } from './performance/plugins/page-visible-state';
import { resourceTimingBuffer } from './performance/resource-timings-buffer';
import { appFramework } from './plugins/app/framework';
import { appVersion } from './plugins/app/version';
import { eventBrowserPlugin } from './plugins/browser';
import { eventCpusPlugin } from './plugins/cpus';
import { featureFlags } from './plugins/feature-flags';
import { eventMemoryPlugin } from './plugins/memory';
import { eventNetworkPlugin } from './plugins/network';
import { ssrFeatureFlags } from './plugins/ssr/feature-flags';
import { ssrSuccess } from './plugins/ssr/success';
import { Route } from './route';
import { getPerformanceObject } from './utils/get-performance-object';
import { getWindowObject } from './utils/get-window-object';
import { isInitialPageLoad } from './utils/is-initial-page-load';

type GasV3 = {
	sendOperationalEvent: (payload: any) => void;
};

export type PublisherSetupAttrs = {
	allowlist?: (key: string, data: ExperienceData | PageLoadExperienceData) => boolean;
	app: AppConfig;
	bundleEvalTimings?: BundleEvalTimingsConfig;
	featureFlagClient?: FeatureFlagClient;
	featureFlags?: string[];
	gasv3: GasV3;
	hostname?: string;
	performanceConfig?: PerformanceConfig;
	plugins?: {
		(data: ExperienceData | PageLoadExperienceData): CustomData | null;
	}[];
	product: string;
	region?: string;
	resourceTimings?: ResourceTimingsConfig;
	ssr?: SSRConfig;
	tenantType?: TenantType;
};

type Payload = {
	action: 'measured';
	actionSubject: 'experience';
	attributes: {
		performance: {
			[key: string]: unknown;
			'event:key': string;
		};
		properties: {
			[key: string]: unknown;
			'event:product': string;
			'event:region': string;
			'event:schema': string;
			'event:source': { name: string; version: string };
			'experience:duration': number;
			'experience:key': string;
			'experience:name': string;
			'experience:spanId'?: string;
			'experience:startTime'?: number;
			'experience:status': string;
			'ufo:tracingContext'?: {
				browserTimeOrigin: number;
				'X-B3-SpanId': string;
				'X-B3-TraceId': string;
			};
		};
		task: string;
	};
	source: 'measured';
	tags: string[];
};

type allowlistType = (key: string, data: ExperienceData | PageLoadExperienceData) => boolean;

const UNKNOWN_VALUE = 'unknown';

const DEFAULT_PAYLOAD_DATA: ExperienceData = {
	schemaVersion: UNKNOWN_VALUE,
	id: UNKNOWN_VALUE,
	uuid: UNKNOWN_VALUE,
	state: {
		id: UNKNOWN_VALUE,
	},
	result: {
		startTime: 0,
		duration: 0,
		success: false,
	},
	type: ExperienceTypes.Experience,
	category: UNKNOWN_VALUE,
	performanceType: ExperiencePerformanceTypes.Custom,
	metadata: {},
	explicitTimings: {},
	metrics: {
		startTime: 0,
		endTime: 0,
		marks: [],
	},
	platform: null,
	children: [],
	pageVisibleState: PageVisibleState.MIXED,
	isSSROutputAsFMP: false,
	timings: [],
	featureFlags: [],
};

class Publisher {
	gasv3: GasV3 | null = null;

	product: string = '';

	region: string = UNKNOWN_VALUE;

	hostname: string = getWindowObject()?.location?.hostname || '';

	tenantType: TenantType = TenantType.RealUser;

	app: AppConfig = { version: { web: UNKNOWN_VALUE } };

	performanceConfig: PerformanceConfig = {};

	buffer: Array<Payload> = [];

	featureFlagClient: FeatureFlagClient | null = null;

	featureFlags: string[] = [];

	ssr: SSRConfig | null = null;

	resourceTimings: ResourceTimingsConfig | null = null;

	bundleEvalTimings: BundleEvalTimingsConfig | null = null;

	route: {
		getRoute(): string;
		route: string;
		setRoute(newRoute: string): void;
	} = Route;

	plugins: { (data: ExperienceData | PageLoadExperienceData): CustomData | null }[] | null = null;

	allowlist: allowlistType = () => true;

	publish(data: ExperienceData | PageLoadExperienceData): void {
		const payloadStart = getPerformanceObject()?.now() || 0;
		const experienceKey = this.getExperienceKey(data);

		if (!this.allowlist(experienceKey, data)) {
			ufowarn(`experience ${experienceKey} does not meet requirements of allowlist`);
			return;
		}

		// it is used to setup default properties on the data to prevent errors
		const supportedData = { ...DEFAULT_PAYLOAD_DATA, ...data };

		const payload: Payload = {
			actionSubject: 'experience',
			action: 'measured',
			source: 'measured',
			tags: ['observability'],
			attributes: {
				properties: {
					'event:source': {
						name: 'ufo/web',
						version: supportedData.schemaVersion,
					},
					'event:schema': UFO_BUILD_VERSION || UNKNOWN_VALUE,
					'event:product': this.product,
					'event:region': this.region,
					'event:hostname': this.hostname,
					'event:route': this.route.getRoute(),
					...eventBrowserPlugin(),
					...eventCpusPlugin(),
					...eventMemoryPlugin(),
					...eventNetworkPlugin(),
					...appFramework(this.app),
					...appVersion(this.app),
					'experience:name': supportedData.id,
					// platform.fe.page-load|interaction|custom|page-segment.editor
					'experience:key': experienceKey,
					'experience:status': supportedData.state.id,
					'experience:duration': supportedData.result.duration,
					...(supportedData.result.startTime !== null
						? {
								'experience:startTime': supportedData.result.startTime,
							}
						: null),
					...(supportedData.uuid ? { 'experience:spanId': supportedData.uuid } : null),
					...(this.tenantType !== TenantType.RealUser
						? { 'event:tenantType': this.tenantType }
						: null),
					...this.mapMetadataToPayload(supportedData.metadata),
					...this.extractPageLoadData(supportedData),
					...pageVisibleState(supportedData),
					...(isInitialPageLoad(supportedData) && ssrSuccess(this.ssr)),
					...(isInitialPageLoad(supportedData) && ssrFeatureFlags(this.ssr)),
					...(this.featureFlagClient &&
						featureFlags(this.featureFlagClient, this.featureFlags, supportedData)),
				},
				performance: {
					...this.getPerformanceMetrics(supportedData),
					...(supportedData.traceContext
						? {
								'ufo:tracingContext': {
									'X-B3-TraceId': supportedData.traceContext.traceId,
									'X-B3-SpanId': supportedData.traceContext.spanId,
									browserTimeOrigin: getPerformanceObject()?.timeOrigin ?? 0,
								},
							}
						: null),
				},
				task: supportedData.id,
			},
		};

		const payloadEnd = getPerformanceObject()?.now() || 0;
		payload.attributes.performance.payloadTime = payloadEnd - payloadStart;

		if (this.gasv3) {
			this.gasv3.sendOperationalEvent(payload);
		} else {
			this.buffer.push(payload);
		}

		ufolog('PAYLOAD:', JSON.stringify(payload, null, 2));
	}

	setGlobalEventStream(): void {
		if (!(getGlobalEventStream() instanceof UFOGlobalEventStream)) {
			const eventStream = new UFOGlobalEventStream();
			eventStream.push({
				type: UFOGlobalEventStreamEventType.SUBSCRIBE,
				payload: {
					experienceId: '__SUBSCRIBE_ALL__',
					callback: this.publish.bind(this),
				},
			});
			setGlobalEventStream(eventStream);
		}
	}

	setRoute(newRoute: string): void {
		this.route.setRoute(newRoute);
	}

	setup(config: PublisherSetupAttrs): void {
		visibilityChangeObserver.start();
		resourceTimingBuffer.start();

		if (!this.product) {
			this.product = config.product || '';
		}

		if (config.allowlist) {
			this.allowlist = config.allowlist;
		}

		if (config.tenantType) {
			this.tenantType = config.tenantType;
		}

		if (config.hostname) {
			this.hostname = config.hostname;
		}

		if (config.region) {
			this.region = config.region;
		}

		if (config.app) {
			this.app = config.app;
		}

		if (config.featureFlagClient) {
			this.featureFlagClient = config.featureFlagClient;
		}

		if (config.featureFlags) {
			this.featureFlags = config.featureFlags;
		}

		if (config.ssr) {
			this.ssr = config.ssr;
		}

		if (config.bundleEvalTimings) {
			this.bundleEvalTimings = config.bundleEvalTimings;
		}

		if (config.resourceTimings) {
			this.resourceTimings = config.resourceTimings;
		}

		if (config.performanceConfig) {
			this.performanceConfig = config.performanceConfig;
		}

		if (config.plugins) {
			this.plugins = config.plugins;
		}

		this.setGlobalEventStream();

		if (!this.gasv3) {
			this.gasv3 = config.gasv3;
			this.buffer.forEach(this.gasv3.sendOperationalEvent);
		}
	}

	private mapMetadataToPayload(metadata: CustomData) {
		const payload: CustomData = {};
		Object.entries(metadata).forEach(([key, value]) => {
			if (value !== undefined) {
				payload[`custom:${key}`] = value;
			}
		});
		return payload;
	}

	private extractPageLoadData(data: ExperienceData | PageLoadExperienceData) {
		if ('initial' in data) {
			return {
				'experience:pageLoadType': data.initial ? 'initial' : 'transition',
			};
		}
		return {};
	}

	private getPrefix(data: ExperienceData | PageLoadExperienceData) {
		if (data.platform !== null) {
			return 'platform';
		}
		return this.product;
	}

	private getExperienceKey(data: ExperienceData | PageLoadExperienceData) {
		const keyParts = [this.getPrefix(data), 'fe', data.type];
		if (data.platform) {
			keyParts.push(data.platform.component);
		}
		keyParts.push(data.id);
		return keyParts.join('.');
	}

	private getExperiencePerformanceKey(data: ExperienceData | PageLoadExperienceData) {
		const keyParts = [this.getPrefix(data), 'fe', data.performanceType];
		if (data.platform) {
			keyParts.push(data.platform.component);
		}
		keyParts.push(data.id);
		return keyParts.join('.');
	}

	private getExperiencePerformanceType(data: ExperienceData | PageLoadExperienceData) {
		return data.performanceType;
	}

	private getPerformanceMetrics(data: ExperienceData | PageLoadExperienceData) {
		return {
			'event:key': this.getExperiencePerformanceKey(data),
			'event:type': this.getExperiencePerformanceType(data),
			...getPerformancePayload(
				data,
				this.ssr,
				this.bundleEvalTimings,
				this.resourceTimings,
				this.performanceConfig,
			),
			...this.getPluginsMetrics(data),
		};
	}

	private getPluginsMetrics = (data: ExperienceData | PageLoadExperienceData) => {
		const result: CustomData = {};
		if (this.plugins) {
			this.plugins.forEach((plugin) => {
				const pluginOutput = plugin({ ...data });
				if (pluginOutput === null || typeof pluginOutput !== 'object') {
					return;
				}
				Object.entries(pluginOutput).forEach(([key, value]) => {
					result[`${this.product}:${key}`] = value;
				});
			});
		}
		return result;
	};
}

export const payloadPublisher: Publisher = new Publisher();
